import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSuburb, useCart } from "../App";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Search, Store, ChevronDown, ChevronRight, MapPin, ShoppingCart, Loader2,
  AlertCircle, Package, Filter, X, TrendingDown, SlidersHorizontal, Info,
  SearchX, Mail, Copy, ArrowLeft,
} from "lucide-react";

/* ---------- helpers ---------- */

const getProductImage = (product) => {
  if (product.prices) {
    for (const p of product.prices) {
      if (p.image_url) return p.image_url;
    }
    for (const p of product.prices) {
      if (p.retailer === "woolworths" && p.url) {
        const parts = p.url.split("/");
        const idx = parts.findIndex((x) => x === "productdetails");
        if (idx !== -1 && /^\d+$/.test(parts[idx + 1]))
          return `https://cdn0.woolworths.media/content/wowproductimages/large/${parts[idx + 1]}.jpg`;
      }
      if (p.retailer === "coles" && p.url) {
        const seg = p.url.split("?")[0].replace(/\/$/, "").split("/").pop();
        const id = seg.split("-").pop();
        if (/^\d+$/.test(id))
          return `https://productimages.coles.com.au/productimages/${id[0]}/${id}.jpg`;
      }
    }
  }
  return null;
};

const ProductPlaceholder = ({ brand, name }) => {
  const t = brand || name?.split(" ")[0] || "Product";
  const colors = ["bg-sky-100 text-sky-700","bg-emerald-100 text-emerald-700","bg-amber-100 text-amber-700","bg-rose-100 text-rose-700","bg-violet-100 text-violet-700"];
  return (
    <div className={`w-full h-full flex items-center justify-center ${colors[t.length % 5]}`}>
      <div className="text-center p-2"><Package className="w-8 h-8 mx-auto mb-1 opacity-50" /><span className="text-xs font-medium line-clamp-2">{t}</span></div>
    </div>
  );
};

const RETAILER_CONFIG = {
  coles: { label: "Coles", short: "C", bg: "bg-red-600", badge: "bg-red-100 text-red-700 border-red-200", dot: "bg-red-500" },
  woolworths: { label: "Woolworths", short: "W", bg: "bg-green-600", badge: "bg-green-100 text-green-700 border-green-200", dot: "bg-green-500" },
  chemist_warehouse: { label: "Chemist WH", short: "WH", bg: "bg-blue-600", badge: "bg-blue-100 text-blue-700 border-blue-200", dot: "bg-blue-500" },
};

const fmt = (p) => (typeof p === "number" && p > 0 ? `$${p.toFixed(2)}` : "$--.--");

const PAGE_SIZE = 20; // 20 results per store per page (60 total)

/* ---------- Facet Sidebar Section ---------- */

const FacetSection = ({ title, items, selected, onToggle, testIdPrefix }) => {
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const initialCount = 5;
  const displayItems = showAll ? items : items.slice(0, initialCount);
  const remainingCount = items.length - initialCount;

  if (!items.length) return null;

  return (
    <div className="mb-4" data-testid={`facet-${testIdPrefix}`}>
      <button
        className="flex items-center justify-between w-full text-left text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2"
        onClick={() => setExpanded(!expanded)}
        data-testid={`facet-toggle-${testIdPrefix}`}
      >
        {title}
        {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>
      {expanded && (
        <div className="space-y-1">
          {displayItems.map((item) => (
            <label
              key={item.name}
              className="flex items-center gap-2 cursor-pointer group px-1 py-0.5 rounded hover:bg-slate-50"
              data-testid={`facet-item-${testIdPrefix}-${item.name.replace(/\s/g, "-").toLowerCase()}`}
            >
              <input
                type="checkbox"
                checked={selected.includes(item.name)}
                onChange={() => onToggle(item.name)}
                className="w-3.5 h-3.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
              />
              <span className="text-sm text-slate-600 group-hover:text-slate-900 truncate flex-1">
                {item.name}
              </span>
              <span className="text-[10px] text-slate-400 tabular-nums">{item.count}</span>
            </label>
          ))}
          {remainingCount > 0 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="text-xs text-sky-600 hover:text-sky-700 hover:underline pl-1 cursor-pointer font-medium"
              data-testid={`facet-show-more-${testIdPrefix}`}
            >
              {showAll ? "Show less" : `+${remainingCount} more`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/* ---------- No Results Found Modal ---------- */

const NoResultsModal = ({ isOpen, onClose, searchQuery, onContinueShopping }) => {
  const [copied, setCopied] = useState(false);
  const supportEmail = "support@bundlecart.au";

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(supportEmail);
      setCopied(true);
      toast.success("Email copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy email");
    }
  };

  const handleContinueShopping = () => {
    onContinueShopping();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="no-results-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm" 
        onClick={onClose}
        data-testid="no-results-modal-backdrop"
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-full hover:bg-slate-100 transition-colors"
          data-testid="no-results-modal-close"
        >
          <X className="w-5 h-5 text-slate-400" />
        </button>

        {/* Content */}
        <div className="p-8 text-center">
          {/* Icon */}
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-sky-100 to-blue-100 rounded-full flex items-center justify-center">
            <SearchX className="w-10 h-10 text-sky-600" />
          </div>

          {/* Heading */}
          <h2 className="text-xl font-bold text-slate-900 mb-3" data-testid="no-results-modal-heading">
            Can't find what you're looking for?
          </h2>

          {/* Search query context */}
          {searchQuery && (
            <p className="text-sm text-slate-500 mb-4">
              No results found for "<span className="font-medium text-slate-700">{searchQuery}</span>"
            </p>
          )}

          {/* Body Text */}
          <p className="text-slate-600 mb-6 leading-relaxed">
            We're constantly adding new items to our catalog. If a specific product is missing, let us know!
          </p>

          {/* Email Section */}
          <div className="bg-slate-50 rounded-xl p-4 mb-6">
            <p className="text-sm text-slate-600 mb-3">
              Email us and we'll try to source it for you:
            </p>
            <div className="flex items-center justify-center gap-2">
              <a
                href={`mailto:${supportEmail}?subject=Product Request&body=Hi BundleCart team,%0A%0AI couldn't find the following product:%0A%0A[Please describe the product you're looking for]%0A%0AThanks!`}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-sky-600 hover:bg-sky-700 text-white rounded-lg font-medium text-sm transition-colors"
                data-testid="no-results-email-link"
              >
                <Mail className="w-4 h-4" />
                {supportEmail}
              </a>
              <button
                onClick={handleCopyEmail}
                className={`p-2.5 rounded-lg border transition-all ${
                  copied 
                    ? "bg-green-50 border-green-200 text-green-600" 
                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
                title="Copy email address"
                data-testid="no-results-copy-email"
              >
                <Copy className={`w-4 h-4 ${copied ? "text-green-600" : ""}`} />
              </button>
            </div>
            {copied && (
              <p className="text-xs text-green-600 mt-2 font-medium">Copied!</p>
            )}
          </div>

          {/* Continue Shopping Button */}
          <Button
            onClick={handleContinueShopping}
            variant="outline"
            className="w-full h-11 text-slate-700 hover:bg-slate-50"
            data-testid="no-results-continue-shopping"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Continue Shopping
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ---------- MAIN COMPONENT ---------- */

const ProductsPage = () => {
  const navigate = useNavigate();
  const { suburb, suburbData } = useSuburb();
  const { addToCart } = useCart();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [displayLabel, setDisplayLabel] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [addingToCart, setAddingToCart] = useState({});
  const [stores, setStores] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLanding, setIsLanding] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Prefetch
  const prefetchRef = useRef(null);
  const [prefetchedData, setPrefetchedData] = useState(null);

  // Facets & Filters
  const [facets, setFacets] = useState({});
  const [sortMode, setSortMode] = useState("relevance");
  const [storeFilters, setStoreFilters] = useState({ coles: true, woolworths: true, chemist_warehouse: true });
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // No Results Modal
  const [showNoResultsModal, setShowNoResultsModal] = useState(false);

  // Did-you-mean
  const [didYouMean, setDidYouMean] = useState(null);

  // Redirect if no suburb
  const hasShownToast = useRef(false);
  useEffect(() => {
    if (!suburb && !hasShownToast.current) {
      hasShownToast.current = true;
      toast.error("Please select your suburb first");
      navigate("/");
    }
  }, [suburb, navigate]);

  // Fetch nearby stores
  useEffect(() => {
    const fetchStores = async () => {
      if (!suburb) return;
      try {
        const pc = suburbData?.postcode || "";
        const r = await axios.get(`${API}/stores?suburb=${suburb}&postcode=${pc}`);
        setStores(r.data.stores || []);
      } catch { /* silent */ }
    };
    fetchStores();
  }, [suburb, suburbData]);

  // Load landing products on mount
  useEffect(() => {
    if (!suburb) return;
    // Scroll to top when suburb changes (coming from landing page)
    window.scrollTo({ top: 0, behavior: 'instant' });
    const loadLanding = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/products/landing`);
        const landingProducts = r.data.products || [];
        setProducts(landingProducts);
        setTotalProducts(r.data.total || landingProducts.length);
        setFacets(r.data.facets || {});
        setIsLanding(true);
        setDisplayLabel("Popular Products");
      } catch (err) {
        console.error("Landing load failed:", err);
      } finally {
        setLoading(false);
      }
    };
    loadLanding();
  }, [suburb]);

  // Build store filter string
  const activeStores = useMemo(() => {
    const active = Object.entries(storeFilters).filter(([, v]) => v).map(([k]) => k);
    return active.length === 3 ? null : active.join(",");
  }, [storeFilters]);

  // Prefetch next page silently (Direct Search API)
  const prefetchNextPage = useCallback(async (query, page) => {
    if (prefetchRef.current) return;
    prefetchRef.current = true;
    try {
      const params = { query, page: page + 1, page_size: PAGE_SIZE };
      const r = await axios.get(`${API}/products/direct-search`, { params });
      const newProducts = r.data.products || [];
      if (newProducts.length > 0) {
        // Transform to display format
        const transformed = newProducts.map(p => ({
          id: p.id,
          name: p.product_name || p.name,
          brand: p.brand || "",
          prices: [{
            retailer: p.retailer,
            price: p.current_price,
            image_url: p.image_url,
            url: p.url,
            unit_price: p.unit_price,
            product_size: p.product_size
          }]
        }));
        setPrefetchedData({ products: transformed, page: r.data.page, hasMore: r.data.has_more });
      }
    } catch { /* silent */ }
    finally { prefetchRef.current = false; }
  }, []);

  // Fetch products from backend using Direct Search API (verbatim query to all RapidAPIs)
  const fetchProducts = useCallback(async ({ query, page = 1, append = false }) => {
    if (!query) return;
    if (append) setLoadingMore(true);
    else { setLoading(true); setProducts([]); }

    try {
      // Direct Search: sends exact query string to Coles, Woolworths, Chemist APIs
      const params = { query, page, page_size: PAGE_SIZE };
      const r = await axios.get(`${API}/products/direct-search`, { params });
      
      // Transform API response to display format
      const rawProducts = r.data.products || [];
      const transformedProducts = rawProducts.map(p => ({
        id: p.id,
        name: p.product_name || p.name,
        brand: p.brand || "",
        product_size: p.product_size || "",
        prices: [{
          retailer: p.retailer,
          price: p.current_price,
          image_url: p.image_url,
          url: p.url,
          unit_price: p.unit_price,
          product_size: p.product_size
        }]
      }));

      if (append) {
        setProducts((prev) => [...prev, ...transformedProducts]);
        // Add new products to total count when loading more
        setTotalProducts((prev) => prev + transformedProducts.length);
      } else {
        setProducts(transformedProducts);
        setTotalProducts(r.data.total_products || transformedProducts.length);
      }

      setSearchQuery(query);
      setDisplayLabel(query);
      setCurrentPage(r.data.page || page);
      setHasMore(r.data.has_more || false);
      setHasSearched(true);
      setIsLanding(false);
      
      if (!append) {
        // Set facets from direct search response
        setFacets(r.data.facets || {});
        setDidYouMean(null);
        // Reset filters on new search
        setSelectedBrands([]);
        setSelectedSizes([]);
        setSelectedTypes([]);
        
        // Show no results modal if search returned 0 products
        if (transformedProducts.length === 0) {
          setShowNoResultsModal(true);
        }
      }

      // Prefetch next page if available
      if (r.data.has_more && page === 1) {
        setPrefetchedData(null);
        setTimeout(() => prefetchNextPage(query, page), 1500);
      }
    } catch (err) {
      console.error("Fetch failed:", err);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [prefetchNextPage]);

  // Refetch when suburb changes (direct search doesn't use sort/store filters)
  useEffect(() => {
    // Direct search doesn't need to refetch on filter changes
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchInput.trim()) return;
    setSelectedBrands([]);
    setSelectedSizes([]);
    setSelectedTypes([]);
    setSelectedCategories([]);
    setDidYouMean(null);
    setPrefetchedData(null);
    // Scroll to top of page when searching
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchProducts({ query: searchInput.trim() });
  };

  const handleLoadMore = () => {
    if (!hasMore || loadingMore) return;
    if (prefetchedData && prefetchedData.page === currentPage + 1) {
      setProducts((prev) => [...prev, ...prefetchedData.products]);
      setCurrentPage(prefetchedData.page);
      setHasMore(prefetchedData.hasMore);
      // Update total count when using prefetched data
      setTotalProducts((prev) => prev + prefetchedData.products.length);
      setPrefetchedData(null);
      if (prefetchedData.hasMore) {
        setTimeout(() => prefetchNextPage(searchQuery, prefetchedData.page), 500);
      }
      return;
    }
    fetchProducts({ query: searchQuery, page: currentPage + 1, append: true });
  };

  const handleAddToCart = async (product, priceInfo) => {
    const key = `${product.id}-${priceInfo.retailer}`;
    setAddingToCart((p) => ({ ...p, [key]: true }));
    try {
      await addToCart({
        product_id: product.id, name: product.name, brand: product.brand,
        size: product.size, price: priceInfo.price, retailer: priceInfo.retailer,
        url: priceInfo.url, image_url: priceInfo.image_url || getProductImage(product),
        store_id: priceInfo.retailer,
      });
    } catch { /* handled by addToCart */ }
    finally { setAddingToCart((p) => ({ ...p, [key]: false })); }
  };

  const toggleStore = (store) => {
    const updated = { ...storeFilters, [store]: !storeFilters[store] };
    if (!Object.values(updated).some(Boolean)) return;
    setStoreFilters(updated);
  };

  const toggleBrand = (brand) => {
    setSelectedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]
    );
  };

  const toggleSize = (size) => {
    setSelectedSizes((prev) =>
      prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size]
    );
  };

  const toggleType = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const toggleCategory = (cat) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  // Client-side filtering for multi-select (all filtering done here)
  const displayProducts = useMemo(() => {
    let filtered = products;

    // Store filter (applied to prices within each product)
    filtered = filtered.map((p) => {
      const filteredPrices = (p.prices || []).filter((pr) => storeFilters[pr.retailer]);
      if (filteredPrices.length === 0) return null;
      return { ...p, prices: filteredPrices };
    }).filter(Boolean);

    // Brand filter (multi-select: show products matching ANY selected brand)
    if (selectedBrands.length > 0) {
      filtered = filtered.filter((p) => {
        const brand = (p.brand || "").trim();
        return selectedBrands.includes(brand);
      });
    }

    // Size filter (multi-select: show products matching ANY selected size)
    if (selectedSizes.length > 0) {
      filtered = filtered.filter((p) => {
        const size = (p.size || "").trim();
        return selectedSizes.includes(size);
      });
    }

    // Type filter (multi-select: show products matching ANY selected type keyword)
    if (selectedTypes.length > 0) {
      const TYPE_KEYWORDS = {
        "Skim": ["skim"], "Lite": ["lite", "light"],
        "Full Cream": ["full cream", "full-cream"],
        "Semi Skim": ["semi skim", "semi-skim"],
        "Low Fat": ["low fat", "low-fat", "reduced fat", "reduced-fat"],
        "No Fat": ["no fat", "fat free", "fat-free"],
        "Lactose Free": ["lactose free", "lactose-free"],
        "Organic": ["organic"], "Gluten Free": ["gluten free", "gluten-free"],
        "Wholemeal": ["wholemeal", "whole meal", "whole wheat", "wholewheat"],
        "Multigrain": ["multigrain", "multi grain", "multi-grain"],
        "Sourdough": ["sourdough"], "White": ["white bread", "white sliced"],
        "Free Range": ["free range", "free-range"],
        "Cage Free": ["cage free", "cage-free"],
        "Unsalted": ["unsalted"], "Salted": ["salted"],
        "Sugar Free": ["sugar free", "sugar-free", "no sugar", "no added sugar"],
        "Diet": ["diet"], "Zero": ["zero"], "Sparkling": ["sparkling"],
        "Still": ["still water"], "Soy": ["soy"], "Almond": ["almond"],
        "Oat": ["oat"], "Coconut": ["coconut"], "A2": ["a2"],
        "Greek": ["greek"], "Natural": ["natural"],
        "Flavoured": ["flavoured", "flavored"],
      };
      filtered = filtered.filter((p) => {
        const name = (p.name || "").toLowerCase();
        return selectedTypes.some((type) => {
          const keywords = TYPE_KEYWORDS[type] || [type.toLowerCase()];
          return keywords.some((kw) => name.includes(kw));
        });
      });
    }

    // Category filter (multi-select: show products matching ANY selected category)
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((p) => {
        const cat = (p.category || "Other").trim();
        return selectedCategories.includes(cat);
      });
    }

    // Sort
    if (sortMode === "price_low") {
      filtered.sort((a, b) => {
        const aMin = Math.min(...(a.prices || []).filter(p => p.price > 0).map(p => p.price), 999999);
        const bMin = Math.min(...(b.prices || []).filter(p => p.price > 0).map(p => p.price), 999999);
        return aMin - bMin;
      });
    } else if (sortMode === "price_high") {
      filtered.sort((a, b) => {
        const aMax = Math.max(...(a.prices || []).map(p => p.price), 0);
        const bMax = Math.max(...(b.prices || []).map(p => p.price), 0);
        return bMax - aMax;
      });
    }

    return filtered;
  }, [products, storeFilters, selectedBrands, selectedSizes, selectedTypes, selectedCategories, sortMode]);

  const hasProducts = products.length > 0;
  const hasActiveFacets = selectedBrands.length > 0 || selectedSizes.length > 0 || selectedTypes.length > 0 || selectedCategories.length > 0;

  const clearAllFacets = () => {
    setSelectedBrands([]);
    setSelectedSizes([]);
    setSelectedTypes([]);
    setSelectedCategories([]);
  };

  // Handler for "Continue Shopping" - clears search and shows landing products
  const handleContinueShopping = useCallback(async () => {
    setSearchInput("");
    setSearchQuery("");
    setDisplayLabel("");
    setHasSearched(false);
    clearAllFacets();
    setStoreFilters({ coles: true, woolworths: true, chemist_warehouse: true });
    setDidYouMean(null);
    
    // Reload landing products
    setLoading(true);
    try {
      const r = await axios.get(`${API}/products/landing`);
      const landingProducts = r.data.products || [];
      setProducts(landingProducts);
      setTotalProducts(r.data.total || landingProducts.length);
      setFacets(r.data.facets || {});
      setIsLanding(true);
      setDisplayLabel("Popular Products");
    } catch (err) {
      console.error("Landing reload failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  /* ---------- Sidebar Content ---------- */
  const SidebarContent = () => (
    <div className="space-y-1">
      {/* Store Filters */}
      <div className="mb-4" data-testid="sidebar-store-filters">
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Stores</h3>
        <div className="space-y-1">
          {Object.entries(RETAILER_CONFIG).map(([key, cfg]) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer group px-1 py-0.5 rounded hover:bg-slate-50">
              <input
                type="checkbox"
                checked={storeFilters[key]}
                onChange={() => toggleStore(key)}
                className="w-3.5 h-3.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                data-testid={`sidebar-store-${key}`}
              />
              <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
              <span className="text-sm text-slate-600 group-hover:text-slate-900">{cfg.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="border-t border-slate-200 my-3" />

      {/* Brand Facets */}
      <FacetSection title="Brand" items={facets.brands || []} selected={selectedBrands} onToggle={toggleBrand} testIdPrefix="brand" />

      {/* Category Facets */}
      <FacetSection title="Category" items={facets.categories || []} selected={selectedCategories} onToggle={toggleCategory} testIdPrefix="category" />

      {/* Type Facets */}
      <FacetSection title="Type" items={facets.types || []} selected={selectedTypes} onToggle={toggleType} testIdPrefix="type" />

      {/* Size Facets */}
      <FacetSection title="Size / Volume" items={facets.sizes || []} selected={selectedSizes} onToggle={toggleSize} testIdPrefix="size" />

      {hasActiveFacets && (
        <Button variant="ghost" size="sm" className="w-full mt-2 text-xs text-slate-500" onClick={clearAllFacets} data-testid="clear-all-facets">
          <X className="w-3 h-3 mr-1" /> Clear all filters
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50">
      {/* Sticky Search Header - positioned below the main header */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-50 border-sky-200">
                <MapPin className="w-3.5 h-3.5 text-sky-600" />
                <span className="font-medium text-sky-700">{suburb}</span>
              </Badge>
              <span className="flex items-center gap-1 text-sm text-slate-500">
                <Store className="w-4 h-4" />{stores.length} stores nearby
              </span>
            </div>
            <form onSubmit={handleSearch} className="flex-1 max-w-lg">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search products... (e.g. 'Milk 2L', 'Woolworths Bread')" value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-10 pr-20" data-testid="product-search-input" />
                <Button type="submit" size="sm" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 bg-sky-600 hover:bg-sky-700" data-testid="search-btn">
                  Search
                </Button>
              </div>
            </form>
            {/* Sort & mobile filter toggle */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                className="h-8 text-xs lg:hidden"
                onClick={() => setShowMobileSidebar(true)}
                data-testid="mobile-filter-toggle"
              >
                <SlidersHorizontal className="w-3.5 h-3.5 mr-1" />
                Filters
                {hasActiveFacets && <span className="ml-1 w-4 h-4 rounded-full bg-sky-600 text-white text-[10px] flex items-center justify-center">{selectedBrands.length + selectedSizes.length + selectedTypes.length + selectedCategories.length}</span>}
              </Button>
              <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}
                className="h-8 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700"
                data-testid="sort-select">
                <option value="relevance">Default</option>
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {showMobileSidebar && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-sidebar-overlay">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowMobileSidebar(false)} />
          <div className="absolute right-0 top-0 h-full w-72 bg-white shadow-xl overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-slate-900">Filters</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowMobileSidebar(false)} data-testid="close-mobile-sidebar">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <SidebarContent />
          </div>
        </div>
      )}

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 py-5">
        <div className="flex gap-6">

          {/* Desktop Sidebar — show when we have products (landing or search) */}
          {hasProducts && (
            <aside className="hidden lg:block w-56 flex-shrink-0" data-testid="desktop-sidebar">
              <div className="sticky top-[140px] bg-white rounded-xl border border-slate-200 p-4 shadow-sm max-h-[calc(100vh-160px)] overflow-y-auto">
                <h2 className="font-semibold text-sm text-slate-900 mb-3 flex items-center gap-1.5">
                  <Filter className="w-4 h-4 text-sky-600" /> Filters
                </h2>
                <SidebarContent />
              </div>
            </aside>
          )}

          {/* Product Grid Area */}
          <div className="flex-1 min-w-0">

            {/* Results Header */}
            {hasProducts && !loading && (
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-xl font-bold text-slate-900" data-testid="results-title">
                    {isLanding ? "Popular Products" : `Results for "${displayLabel}"`}
                  </h1>
                  <p className="text-sm text-slate-500 mt-0.5" data-testid="results-counter">
                    {displayProducts.length} of {totalProducts} products
                    {sortMode !== "relevance" && ` · Sorted by ${sortMode === "price_low" ? "price (low)" : "price (high)"}`}
                  </p>
                </div>
                {hasActiveFacets && (
                  <div className="hidden lg:flex items-center gap-1.5 flex-wrap">
                    {selectedBrands.map((b) => (
                      <Badge key={b} variant="secondary" className="text-[10px] h-5 gap-1 cursor-pointer" onClick={() => toggleBrand(b)}>
                        {b} <X className="w-2.5 h-2.5" />
                      </Badge>
                    ))}
                    {selectedTypes.map((t) => (
                      <Badge key={t} variant="secondary" className="text-[10px] h-5 gap-1 cursor-pointer bg-purple-100 text-purple-700" onClick={() => toggleType(t)}>
                        {t} <X className="w-2.5 h-2.5" />
                      </Badge>
                    ))}
                    {selectedCategories.map((c) => (
                      <Badge key={c} variant="secondary" className="text-[10px] h-5 gap-1 cursor-pointer bg-amber-100 text-amber-700" onClick={() => toggleCategory(c)}>
                        {c} <X className="w-2.5 h-2.5" />
                      </Badge>
                    ))}
                    {selectedSizes.map((s) => (
                      <Badge key={s} variant="secondary" className="text-[10px] h-5 gap-1 cursor-pointer" onClick={() => toggleSize(s)}>
                        {s} <X className="w-2.5 h-2.5" />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Did You Mean Banner */}
            {didYouMean && (
              <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2.5" data-testid="did-you-mean-banner">
                <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-800">{didYouMean.message}</p>
              </div>
            )}

            {/* Loading */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                    <div className="h-28 bg-slate-200 rounded mb-3" />
                    <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-slate-200 rounded w-1/2 mb-3" />
                    <div className="space-y-2"><div className="h-10 bg-slate-100 rounded" /><div className="h-10 bg-slate-100 rounded" /></div>
                  </div>
                ))}
              </div>
            ) : displayProducts.length === 0 && hasProducts ? (
              <div className="text-center py-16">
                <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-slate-700" data-testid="no-results-title">No products match your filters</h3>
                <p className="text-slate-500 mt-1 text-sm">Try adjusting your filters or clear them to see all products</p>
                {hasActiveFacets && (
                  <Button variant="outline" size="sm" className="mt-3" onClick={clearAllFacets} data-testid="clear-filters-empty">
                    Clear all filters
                  </Button>
                )}
              </div>
            ) : displayProducts.length === 0 && !hasProducts && !loading ? (
              <div className="text-center py-20" data-testid="search-prompt">
                <Search className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-slate-800 mb-2">Search for products</h2>
                <p className="text-slate-500 text-sm max-w-md mx-auto">
                  Type a keyword like "milk", "bread 2L", or "Woolworths chicken" to find and compare prices across stores.
                </p>
              </div>
            ) : (
              <>
                {/* Product Grid */}
                {displayProducts.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {displayProducts.map((product, index) => {
                      const img = getProductImage(product);
                      const prices = product.prices || [];
                      const lowestPrice = prices.length ? Math.min(...prices.filter(p => p.price > 0).map(p => p.price)) : 0;
                      const hasMultipleStores = prices.length > 1;

                      return (
                        <div key={`${product.id}-${index}`} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`product-card-${index}`}>
                          {/* Image */}
                          <div className="h-28 bg-slate-50 relative overflow-hidden">
                            {img ? (
                              <img src={img} alt={product.name} className="w-full h-full object-contain"
                                onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
                            ) : null}
                            <div className={`absolute inset-0 ${img ? "hidden" : "flex"}`} style={{ display: img ? "none" : "flex" }}>
                              <ProductPlaceholder brand={product.brand} name={product.name} />
                            </div>
                            {hasMultipleStores && (
                              <div className="absolute top-2 right-2">
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-500 text-white text-[10px] font-bold">
                                  <TrendingDown className="w-2.5 h-2.5" />Compare
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Product Info */}
                          <div className="p-3 border-b border-slate-100">
                            <h3 className="font-semibold text-slate-900 text-sm line-clamp-2">{product.name}</h3>
                            <p className="text-xs text-slate-500 mt-0.5">{product.brand}{product.size ? ` · ${product.size}` : ""}</p>
                            {product.category && (
                              <Badge variant="outline" className="mt-1.5 text-[10px] h-5">{product.category}</Badge>
                            )}
                          </div>

                          {/* Price Comparison */}
                          <div className="p-3">
                            {prices.length > 0 ? (
                              <div className="space-y-2">
                                {prices.map((priceInfo, idx) => {
                                  const cfg = RETAILER_CONFIG[priceInfo.retailer] || {};
                                  const cartKey = `${product.id}-${priceInfo.retailer}`;
                                  const isLowest = priceInfo.price === lowestPrice && hasMultipleStores;
                                  const isAdding = addingToCart[cartKey];

                                  return (
                                    <div key={`${product.id}-${priceInfo.retailer}-${idx}`}
                                      className={`flex items-center justify-between gap-2 p-2 rounded-lg border ${
                                        isLowest ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-100"
                                      }`}>
                                      <div className="flex items-center gap-2 min-w-0">
                                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-[10px] font-bold text-white ${cfg.bg}`}>
                                          {cfg.short}
                                        </span>
                                        <div className="min-w-0">
                                          <span className={`text-sm font-bold ${isLowest ? "text-emerald-700" : "text-slate-800"}`}>
                                            {fmt(priceInfo.price)}
                                          </span>
                                          {isLowest && <span className="text-[10px] text-emerald-600 font-medium ml-1">Best</span>}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1.5">
                                        {priceInfo.url && (
                                          <a href={priceInfo.url} target="_blank" rel="noopener noreferrer"
                                            className="text-[10px] text-sky-600 hover:underline hidden sm:inline">View</a>
                                        )}
                                        <Button size="sm" className="h-7 text-xs bg-sky-600 hover:bg-sky-700"
                                          onClick={() => handleAddToCart(product, priceInfo)} disabled={isAdding}
                                          data-testid={`add-to-cart-${index}-${priceInfo.retailer}`}>
                                          {isAdding ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShoppingCart className="w-3 h-3 mr-1" />}
                                          Add
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-xs text-slate-400 text-center py-2">No prices for selected stores</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Load More */}
                {hasMore && !isLanding && (
                  <div className="mt-8 text-center">
                    <Button onClick={handleLoadMore} disabled={loadingMore} variant="outline" size="lg"
                      className="min-w-[220px]" data-testid="load-more-button">
                      {loadingMore ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
                      ) : (
                        <>
                          Load More
                          {prefetchedData ? (
                            <span className="ml-2 text-[10px] text-emerald-600">(ready)</span>
                          ) : (
                            <ChevronDown className="w-4 h-4 ml-2" />
                          )}
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-slate-500 mt-2">
                      Showing {displayProducts.length} of {totalProducts} products
                    </p>
                  </div>
                )}

                {!hasMore && displayProducts.length > 0 && !isLanding && (
                  <p className="text-sm text-slate-400 text-center mt-8">
                    All {displayProducts.length} products loaded
                  </p>
                )}

                {/* Price Disclaimer */}
                {displayProducts.length > 0 && (
                  <div className="mt-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-800">
                      <strong>* Price Disclaimer:</strong> Prices are sourced from third-party data and may vary.
                      Prices are verified when items are added to cart.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* No Results Modal */}
      <NoResultsModal
        isOpen={showNoResultsModal}
        onClose={() => setShowNoResultsModal(false)}
        searchQuery={searchQuery}
        onContinueShopping={handleContinueShopping}
      />
    </div>
  );
};

export default ProductsPage;
